import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUserContext } from '../contexts/UserContext';
import { apiGetAutomationList, apiGetOrganizationDetails } from '../helpers/api';
import {
  GetAllAddonsProductList,
  GetAllCard,
  GetAllPurchasedOneTimeProductList,
  GetFreemiumSubscriptionFeatures,
  GetPurchasedOnAddonsList,
  setClientSubscriptionPlans,
  setFailedClientSubscriptionPlansAndAddOns,
  setMainSubscriptionPlansNoDispatch,
} from '../api/subscription/subscription';
import {
  selectIsBrowsingPlans,
  selectPlanList,
  selectUserActivePlanDetails,
  selectUserActiveSubscriptionPlanId,
  selectUserActiveSubscriptionPlanPriceId,
  subscriptionActions,
} from '../redux/slicers/subscriptionSlicer';
import { dispatcher } from '../helpers/projectHelper';
import { removeDuplicateEntriesViaKey } from '../screens/subscriptionComponents/subscriptionHelper';
import { getAppState } from '../redux/store';
import useCurrentOrganizationInfo from './useCurrentOrganizationInfo';
import { TEXTURE_BASE_TYPE, THREE_3D_MODAL_BASE_TYPE } from '../constants/constants';
import { fetchAllPages } from '../helpers/apiHelper';
import { isCuratorPage } from '../helpers/urlHelper';
import { isMattressBuilderPage } from '../components/admin/curator/ProductBuilderComponents/commonHelper';
import {
  REQUESTS_ROUTE_MODELS,
  REQUESTS_ROUTE_PDP,
  RETAIL_AUTOMATION_ROUTE,
  RETAIL_AUTOMATION_ROUTE_V2,
  RETAIL_CONFIGURATOR_ROUTE,
  RETAIL_CONFIGURATOR_VIEWER_ROUTE,
} from '../constants/routePathConstants';

const isRestrictAPICallsOnReloadLocation = () => {
  // add more url where you restrict the api calls
  return [
    RETAIL_AUTOMATION_ROUTE,
    RETAIL_AUTOMATION_ROUTE_V2,
    RETAIL_CONFIGURATOR_ROUTE,
    RETAIL_CONFIGURATOR_VIEWER_ROUTE,
    REQUESTS_ROUTE_MODELS,
    REQUESTS_ROUTE_PDP,
  ].some((a) => window.location.pathname.includes(a));
};

export const updateSubscriptionsData = async (params) => {
  await dispatcher(setClientSubscriptionPlans());
  await dispatcher(setFailedClientSubscriptionPlansAndAddOns());
  await GetAllPurchasedOneTimeProductList(dispatcher);
  const results = await fetchAllPages((page) => {
    return setMainSubscriptionPlansNoDispatch(page);
  });
  if (results?.results?.length > 0) {
    dispatcher(
      subscriptionActions.setPlans(
        results?.results?.filter((_item) => !_item?.is_archived && !_item?.is_feature_product && !_item?.is_ai_imagine_subscription  && !_item?.is_ai_imagine && !_item?.is_ai_imagine_one_time) || []
      )
    );
    setTimeout(setFilteredMainSubscriptionsPlan, 100);
  }
  dispatcher(GetPurchasedOnAddonsList());
  dispatcher(GetAllAddonsProductList(params));
};

export const setFilteredMainSubscriptionsPlan = (id = null, userPlans = []) => {
  const plans = userPlans.length > 0 ? userPlans : selectPlanList(getAppState());
  const activePlan = selectUserActivePlanDetails(getAppState());
  const activePlanId = id || activePlan?.id;
  if (activePlanId) {
    const getPurchasedActivePlan = plans
      ?.map((a) => {
        let updatedPlan = { ...a };
        let pricingList = [];
        if (a?.is_upload_limit_plan) {
          pricingList = updatedPlan?.pricing_details;
        }
        // in case if any product is already purchased in past but now this is not in our main plans, so we are keeping it until it's subscription ended
        const activePlanPricingDetails = updatedPlan?.pricing_details?.filter(
          (a) => a.id === selectUserActiveSubscriptionPlanPriceId(getAppState())
        );
        const activePlanRecurringInterval = activePlanPricingDetails?.[0]?.recurring_interval;
        const nonActivePlanPricingDetails = updatedPlan?.pricing_details?.filter(
          (a) => a?.recurring_interval !== activePlanRecurringInterval
        );

        if (
          updatedPlan.id === selectUserActiveSubscriptionPlanId(getAppState()) &&
          activePlanPricingDetails?.length > 0
        ) {
          const activePricing = [
            {
              ...activePlanPricingDetails?.[0],
              is_default: true,
              is_disabled: false,
              is_archived: false,
            },
          ];
          return {
            ...updatedPlan,
            pricing_details: pricingList?.length
              ? pricingList
              : [...activePricing, ...nonActivePlanPricingDetails],
          };
        }

        return false;
      })
      .filter(Boolean);

    const plansToShow = [
      ...getPurchasedActivePlan,
      ...plans?.filter((a) => a?.id !== getPurchasedActivePlan?.[0]?.id),
    ];

    const updatedPlans = plansToShow
      ?.map((a) => {
        // showing only the is_default or is_textile_plan & is_disabled subscription plans
        let pricingDetails = [];
        if (
          a?.basemodeltype === THREE_3D_MODAL_BASE_TYPE ||
          a?.basemodeltype === TEXTURE_BASE_TYPE
        ) {
          pricingDetails = a?.pricing_details?.filter(
            (b) => !b?.is_disabled && !b?.is_archived && b?.is_default
          );
        } else {
          pricingDetails = a?.pricing_details?.filter(
            (b) => (b?.is_default || b?.is_textile_plan) && !b?.is_disabled && !b?.is_archived
          );
        }

        if (pricingDetails?.length > 0)
          return {
            ...a,
            pricing_details: pricingDetails,
          };
        return false;
      })
      ?.filter(Boolean);

    dispatcher(subscriptionActions.setPlans(updatedPlans));
  } else {
    const updatedPlans = plans
      ?.map((a) => {
        // showing only the is_default or is_textile_plan & is_disabled subscription plans
        let pricingDetails = [];
        if (
          a?.basemodeltype === THREE_3D_MODAL_BASE_TYPE ||
          a?.basemodeltype === TEXTURE_BASE_TYPE
        ) {
          pricingDetails = a?.pricing_details?.filter(
            (b) => !b?.is_disabled && !b?.is_archived && b?.is_default
          );
        } else {
          pricingDetails = a?.pricing_details?.filter(
            (b) => (b?.is_default || b?.is_textile_plan) && !b?.is_disabled && !b?.is_archived
          );
        }
        if (pricingDetails?.length > 0)
          return {
            ...a,
            pricing_details: pricingDetails,
          };
        return false;
      })
      ?.filter(Boolean);
    dispatcher(subscriptionActions.setPlans(updatedPlans));
  }
};

export const useSubscriptionSetupAPICalls = () => {
  const dispatch = useDispatch();
  const { currentUser, setOrgOwnerDetail, isGuestUser } = useUserContext();
  const currentOrg = currentUser?.organization;

  const activePlan = useSelector(selectUserActivePlanDetails);
  const activePlanId = activePlan?.id;
  const isBrowsingPlan = useSelector(selectIsBrowsingPlans);
  const { isFreemiumOrSelfServeUser } = useCurrentOrganizationInfo();

  const getBillingInfo = async () => {
    const res = await apiGetOrganizationDetails();
    if (!res) return;
    const {
      billing_first_name,
      billing_last_name,
      billing_address,
      billing_city,
      billing_country,
      billing_email,
      billing_mobile_number,
      billing_pincode,
      billing_state,
      billing_street,
      owner_details,
      billing_line1,
      billing_line2,
      billing_line3,
    } = res;
    dispatch(
      subscriptionActions.setBillingInfo({
        billing_name: `${billing_first_name || ''} ${billing_last_name || ''}`?.trim() || '',
        billing_first_name: billing_first_name || '',
        billing_last_name: billing_last_name || '',
        billing_address: billing_address || '',
        billing_city: billing_city || '',
        billing_country: billing_country || '',
        billing_email: billing_email || '',
        billing_mobile_number: billing_mobile_number || '',
        billing_pincode: billing_pincode || '',
        billing_state: billing_state || '',
        billing_street: billing_street || '',
        billing_line1: billing_line1 || '',
        billing_line2: billing_line2 || '',
        billing_line3: billing_line3 || '',
      })
    );

    setOrgOwnerDetail(owner_details);
  };

  useEffect(() => {
    if (isMattressBuilderPage() || isRestrictAPICallsOnReloadLocation()) return;
    if (!currentUser) return;
    // dispatch(GetAllAddonsProductList(activePlanId));
    setFilteredMainSubscriptionsPlan(activePlanId);
  }, [activePlanId, isBrowsingPlan]);

  useEffect(() => {
    if (isMattressBuilderPage() || isRestrictAPICallsOnReloadLocation()) return;
    if (!currentOrg || isGuestUser()) return;
    updateSubscriptionsData(isFreemiumOrSelfServeUser ? { is_new_self_serve_plan: true } : {});
  }, [currentOrg, isFreemiumOrSelfServeUser]);

  useEffect(() => {
    if (isCuratorPage() || isRestrictAPICallsOnReloadLocation()) return;
    if (!currentOrg || isGuestUser()) return;
    // await GetFreemiumSubscriptionFeatures(dispatcher);
    Promise.all([dispatch(GetAllCard()), apiGetAutomationList(), getBillingInfo()]);
  }, [currentOrg]);

  return;
};
